/**
 * AuthView — the sign-in card, in the workspace-shell "bone" design language.
 *
 * The root carries the `workspace-shell` class so the Glass & Print token set
 * (cool field canvas, Instrument Sans UI and prose, blue accent)
 * applies before authentication, matching the app the user lands in. The card reads
 * "YAGNI · Agent teams, managed like your engineering team." so sign-in reads as the
 * door of the same product, not a separate artifact.
 */
import type { CSSProperties, FormEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { AuthSessionResponse } from "@yagni/shared";
import { GoogleBrandButton } from "../components/GoogleBrand.js";

interface AuthViewProps {
  onAuthenticated: (session: AuthSessionResponse) => void;
}

function formatErrorMessage(error: string | null): string | null {
  if (!error) {
    return null;
  }

  switch (error) {
    case "access_denied":
      return "Google sign-in was cancelled.";
    case "oauth_state_mismatch":
      return "Your sign-in session expired. Please try again.";
    case "missing_code":
      return "Google did not return an authorization code.";
    case "google_not_configured":
      return "Google sign-in is not configured yet.";
    case "sso_required":
      return "This workspace requires single sign-on. Use your company's SSO to sign in.";
    case "sso_not_available":
      return "Single sign-on isn't available for this sign-in link.";
    case "sso_invalid_response":
      return "Your identity provider returned an invalid response. Try again or contact your admin.";
    case "sso_domain_not_allowed":
      return "That email domain isn't allowed for this workspace's SSO.";
    case "sso_membership_required":
      return "You need an invite to this workspace before signing in with SSO.";
    case "sso_failed":
      return "Single sign-on failed. Try again or contact your admin.";
    case "sso_not_allowed":
      return "This account can't sign in with SSO.";
    case "google_auth_failed":
      return "Google sign-in failed. Please try again.";
    case "verification_required":
      return "Complete the check below, then continue with Google again.";
    case "workspace_suspended":
      return "This workspace is suspended. Contact support@yagni.app if you think this is a mistake.";
    case "signup_limit":
      return "Too many new workspaces were created from this network today. Try again tomorrow, or email support@yagni.app.";
    case "signup_closed":
      return "New sign-ups are paused right now. If a teammate invited you, use the link in your invite.";
    default:
      return "Google sign-in failed. Please try again.";
  }
}

// Mirrors the backend validator so a tampered marketing-side rewrite can't
// smuggle anything funky into the /api/auth/google URL we build.
const MAX_ANON_ID_LENGTH = 128;
const ANON_ID_PATTERN = /^[A-Za-z0-9._:$-]+$/;

function sanitizeAnonId(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_ANON_ID_LENGTH) return null;
  if (!ANON_ID_PATTERN.test(trimmed)) return null;
  return trimmed;
}

// "Claim this Team" public share token (self-serve trial funnel §4.3). Mirrors
// the backend validator (readSafeClaimToken): base64url alphabet, hard length
// bound. Passed through to /api/auth/google so the callback can seed the new
// workspace from the compiled artifact.
const MAX_CLAIM_TOKEN_LENGTH = 128;
const CLAIM_TOKEN_PATTERN = /^[A-Za-z0-9_-]+$/;

export function sanitizeClaimToken(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_CLAIM_TOKEN_LENGTH) return null;
  if (!CLAIM_TOKEN_PATTERN.test(trimmed)) return null;
  return trimmed;
}

export function sanitizeReturnTo(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return null;
  // Match the backend's same-origin redirect guard.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F:\\@|]/.test(trimmed)) return null;
  return trimmed;
}

// Cloudflare Turnstile, rendered explicitly into our own container. The
// backend asks for it (GET /api/auth/turnstile returns a site key) only when
// it enforces the check, so dev and tests never load the script.
const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
    },
  ) => string;
  remove: (widgetId: string) => void;
}

function readTurnstile(): TurnstileApi | undefined {
  return (window as { turnstile?: TurnstileApi }).turnstile;
}

function loadTurnstile(): Promise<TurnstileApi> {
  const ready = readTurnstile();
  if (ready) return Promise.resolve(ready);
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      const api = readTurnstile();
      if (api) resolve(api);
      else reject(new Error("Turnstile did not load"));
    };
    script.onerror = () => reject(new Error("Turnstile did not load"));
    document.head.appendChild(script);
  });
}

// The token the sign-in link carries. Null until the check passes, and
// again when it expires; a missing token still signs existing users in.
function useTurnstileToken(container: React.RefObject<HTMLDivElement | null>) {
  const [siteKey, setSiteKey] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/auth/turnstile")
      .then((res) => (res.ok ? (res.json() as Promise<{ siteKey?: string | null }>) : null))
      .then((body) => {
        if (!cancelled && typeof body?.siteKey === "string") setSiteKey(body.siteKey);
      })
      .catch(() => {
        // No key means no widget; the backend decides what that allows.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!siteKey || !container.current) return;
    const el = container.current;
    let widgetId: string | null = null;
    let api: TurnstileApi | null = null;
    let cancelled = false;
    loadTurnstile()
      .then((loaded) => {
        if (cancelled) return;
        api = loaded;
        widgetId = loaded.render(el, {
          sitekey: siteKey,
          callback: setToken,
          "expired-callback": () => setToken(null),
          "error-callback": () => setToken(null),
        });
      })
      .catch(() => {
        // Script blocked or offline: sign-in still works for existing users.
      });
    return () => {
      cancelled = true;
      if (api && widgetId) api.remove(widgetId);
    };
  }, [siteKey, container]);

  return { siteKey, token };
}

function readBrowserTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export function AuthView({ onAuthenticated: _onAuthenticated }: AuthViewProps) {
  const error = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return formatErrorMessage(params.get("error"));
  }, []);

  // ── SSO discovery ─ entry under the Google button. POSTs the email to
  // /api/auth/saml/discover; a hit redirects to the connection's same-origin
  // login path, a 404 (deliberately generic server-side) shows the inline
  // "not available" line.
  const [ssoOpen, setSsoOpen] = useState(false);
  const [ssoEmail, setSsoEmail] = useState("");
  const [ssoBusy, setSsoBusy] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);

  const handleSsoSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = ssoEmail.trim();
    if (email.length === 0 || ssoBusy) return;
    setSsoBusy(true);
    setSsoError(null);
    try {
      // Same deep-link pass-through as the Google button: /auth?returnTo=/x
      // rides the SAML round-trip as RelayState and lands the user on /x.
      const returnTo = sanitizeReturnTo(
        new URLSearchParams(window.location.search).get("returnTo"),
      );
      const res = await fetch("/api/auth/saml/discover", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(returnTo ? { email, returnTo } : { email }),
      });
      if (res.ok) {
        const body = (await res.json()) as { loginUrl?: string };
        // The API returns a same-origin path (/api/auth/saml/<id>/login);
        // refuse anything absolute so a bad payload can't redirect off-site.
        if (typeof body.loginUrl === "string" && body.loginUrl.startsWith("/")) {
          window.location.assign(body.loginUrl);
          return;
        }
        setSsoError("Single sign-on failed. Try again or contact your admin.");
      } else if (res.status === 404) {
        setSsoError("Single sign-on is not available for that email domain.");
      } else {
        setSsoError("Single sign-on failed. Try again or contact your admin.");
      }
    } catch {
      setSsoError("Single sign-on failed. Try again or contact your admin.");
    } finally {
      setSsoBusy(false);
    }
  };

  const turnstileContainer = useRef<HTMLDivElement | null>(null);
  const turnstile = useTurnstileToken(turnstileContainer);

  const googleSignInHref = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const anonId = sanitizeAnonId(params.get("mp_anon_id"));
    const claimToken = sanitizeClaimToken(params.get("claim"));
    const returnTo = sanitizeReturnTo(params.get("returnTo"));
    const browserTimezone = readBrowserTimezone();
    const q = new URLSearchParams();
    if (anonId) q.set("mp_anon_id", anonId);
    if (claimToken) {
      q.set("claim", claimToken);
      // Existing users already have a workspace, so OAuth must return to the
      // explicit import confirmation instead of dropping the claim on Work.
      q.set("returnTo", `/auth?claim=${claimToken}`);
    } else if (returnTo) {
      q.set("returnTo", returnTo);
    }
    if (browserTimezone) q.set("timezone", browserTimezone);
    if (turnstile.token) q.set("cf_turnstile", turnstile.token);
    const query = q.toString();
    return query ? `/api/auth/google?${query}` : "/api/auth/google";
  }, [turnstile.token]);

  return (
    <div className="workspace-shell" style={pageStyle} data-testid="auth-view">
      <main style={cardStyle} aria-labelledby="auth-wordmark">
        {/* ── Wordmark ─ mirrors the app sidebar ─ */}
        <header
          className="auth-rise"
          style={{ ...wordmarkBlockStyle, animationDelay: "80ms" }}
        >
          <h1 id="auth-wordmark" style={wordmarkStyle}>
            YAGNI
          </h1>
          <p style={wordmarkSubStyle}>Agent teams, managed like your engineering team.</p>
        </header>

        <div
          className="auth-rise"
          style={{ ...ruleStyle, animationDelay: "240ms" }}
          aria-hidden
        />

        <p className="auth-rise" style={{ ...inviteStyle, animationDelay: "320ms" }}>
          Sign in to open Work.
        </p>

        {/* ── Error notice ─ */}
        {error ? (
          <div
            className="auth-rise"
            role="alert"
            style={{ ...noticeStyle, animationDelay: "420ms" }}
          >
            <span style={noticeTagStyle}>Notice</span>
            <span style={noticeBodyStyle}>{error}</span>
          </div>
        ) : null}

        {/* ── The button (Google-branded per their spec) ─ */}
        <div
          className="auth-rise"
          style={{ ...buttonWrapStyle, animationDelay: "480ms" }}
        >
          <GoogleBrandButton href={googleSignInHref} />
        </div>

        {/* ── Bot check ─ only when the backend enforces it ─ */}
        {turnstile.siteKey ? (
          <div
            ref={turnstileContainer}
            data-testid="turnstile-check"
            style={turnstileStyle}
          />
        ) : null}

        {/* ── SSO entry ─ peer sign-in path that expands to the discovery form ─ */}
        <div
          className="auth-rise"
          style={{ ...ssoBlockStyle, animationDelay: "540ms" }}
        >
          <div style={ssoDividerStyle} aria-hidden>
            <span style={ssoDividerLineStyle} />
            <span style={ssoDividerWordStyle}>or</span>
            <span style={ssoDividerLineStyle} />
          </div>
          {ssoOpen ? (
            <form onSubmit={(e) => void handleSsoSubmit(e)} style={ssoFormStyle}>
              <label htmlFor="sso-email" style={ssoLabelStyle}>
                Work email
              </label>
              <div style={ssoRowStyle}>
                <input
                  id="sso-email"
                  type="email"
                  required
                  autoFocus
                  placeholder="you@company.com"
                  value={ssoEmail}
                  onChange={(e) => setSsoEmail(e.target.value)}
                  disabled={ssoBusy}
                  style={ssoInputStyle}
                />
                <button type="submit" disabled={ssoBusy} style={ssoSubmitStyle}>
                  Continue
                </button>
              </div>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setSsoOpen(true)}
              style={ssoLinkStyle}
            >
              Use single sign-on
            </button>
          )}
          {ssoError ? (
            <p role="alert" style={ssoErrorStyle}>
              {ssoError}
            </p>
          ) : null}
        </div>
      </main>
    </div>
  );
}

// ── styles ────────────────────────────────────────────────────────────────

const pageStyle: CSSProperties = {
  minHeight: "100%",
  display: "grid",
  placeItems: "center",
  padding: "32px 16px",
  background: "var(--canvas-field)",
  position: "relative",
  zIndex: 1,
};

const cardStyle: CSSProperties = {
  width: "100%",
  maxWidth: 440,
  padding: "26px clamp(26px, 5vw, 40px) 36px",
  background: "var(--paper-card)",
  border: "1px solid var(--print-border)",
  borderRadius: 14,
  // Print material with a gentle lift over the field.
  boxShadow:
    "0 1px 2px rgba(45, 55, 90, 0.05), 0 20px 48px -24px rgba(45, 55, 95, 0.22)",
};

// ── Wordmark ──
const wordmarkBlockStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const wordmarkStyle: CSSProperties = {
  margin: 0,
  fontFamily: "var(--display)",
  fontWeight: 700,
  fontSize: "clamp(34px, 7vw, 44px)",
  lineHeight: 1.05,
  letterSpacing: "-0.02em",
  color: "var(--text-primary)",
};

const wordmarkSubStyle: CSSProperties = {
  margin: 0,
  fontFamily: "var(--mono)",
  fontSize: 10,
  letterSpacing: "0.26em",
  textTransform: "uppercase",
  color: "var(--text-tertiary)",
};

// ── Rule ──
const ruleStyle: CSSProperties = {
  height: 1,
  background: "var(--border)",
  margin: "20px 0 18px",
};

// ── Invite ──
const inviteStyle: CSSProperties = {
  margin: "8px 0 24px",
  fontFamily: "var(--body)",
  fontSize: 13.5,
  lineHeight: 1.5,
  color: "var(--text-secondary)",
};

// ── Error notice ──
const noticeStyle: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  padding: "10px 12px",
  margin: "0 0 16px",
  borderRadius: 8,
  border: "1px solid var(--border)",
  borderLeft: "3px solid var(--error)",
  background: "var(--error-muted)",
};

const noticeTagStyle: CSSProperties = {
  fontFamily: "var(--mono)",
  fontSize: 9,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: "var(--error)",
  paddingTop: 2,
  flexShrink: 0,
};

const noticeBodyStyle: CSSProperties = {
  fontFamily: "var(--body)",
  fontSize: 13,
  lineHeight: 1.5,
  color: "var(--ink-soft)",
};

// ── Button wrap ──
const buttonWrapStyle: CSSProperties = {
  display: "flex",
};

// ── Bot check ──
const turnstileStyle: CSSProperties = {
  marginTop: 12,
  minHeight: 65,
};

// ── SSO entry ──
const ssoBlockStyle: CSSProperties = {
  marginTop: 16,
};

const ssoDividerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  margin: "0 0 14px",
};

const ssoDividerLineStyle: CSSProperties = {
  flex: 1,
  height: 1,
  background: "var(--border)",
};

const ssoDividerWordStyle: CSSProperties = {
  fontFamily: "var(--mono)",
  fontSize: 9,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: "var(--text-tertiary)",
};

const ssoLinkStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 40,
  padding: "0 20px",
  borderRadius: 20,
  border: "1px solid var(--border)",
  background: "var(--paper-card)",
  cursor: "pointer",
  fontFamily: "var(--body)",
  fontSize: 13.5,
  fontWeight: 500,
  color: "var(--text-primary)",
};

const ssoFormStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const ssoLabelStyle: CSSProperties = {
  fontFamily: "var(--mono)",
  fontSize: 9,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: "var(--text-tertiary)",
};

const ssoRowStyle: CSSProperties = {
  display: "flex",
  gap: 8,
};

const ssoInputStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  height: 36,
  padding: "0 10px",
  borderRadius: 8,
  border: "1px solid var(--border)",
  background: "var(--bg-input)",
  fontFamily: "var(--body)",
  fontSize: 13,
  color: "var(--text-primary)",
};

const ssoSubmitStyle: CSSProperties = {
  height: 36,
  padding: "0 14px",
  borderRadius: 8,
  border: "1px solid var(--border)",
  background: "var(--paper-card)",
  cursor: "pointer",
  fontFamily: "var(--body)",
  fontSize: 13,
  fontWeight: 500,
  color: "var(--text-primary)",
};

const ssoErrorStyle: CSSProperties = {
  margin: "8px 0 0",
  fontFamily: "var(--body)",
  fontSize: 12.5,
  lineHeight: 1.5,
  color: "var(--error)",
};
